"use client";
import { useState, useMemo } from "react";
import Link from "next/link";
import { MapPin, Clock, Tag } from "lucide-react";
import { ICMS } from "@/store/home/homeModel";
import { cleanHeaderTags, createLookupObject, decodeHtml } from "@/lib/helper";
import Banner from "@/components/Banner";
import { OptimizedImage } from "@/components/common/OptimizedImage";

const categories = [
  "All",
  "Wildlife",
  "Temples",
  "Beaches",
  "Nature",
  "Culture",
  "One-day trips",
];

interface AttractionsProps {
  cmsData: ICMS[];
  bannerData: any[];
  attractionsData: any[];
}

export default function Attractions({
  cmsData,
  bannerData,
  attractionsData,
}: AttractionsProps) {
  const [selectedCategory, setSelectedCategory] = useState("All");
  const lookUpCMSData = useMemo(() => {
    return createLookupObject(cmsData || [], "Name");
  }, [cmsData]);

  const banner =
    bannerData?.filter((banner: any) => banner?.BannerType === "attractions") ||
    [];
  const filteredAttractions =
    selectedCategory === "All"
      ? attractionsData
      : attractionsData.filter(
          (a: any) => a.AttractionCategory === selectedCategory,
        );
  const featuredAttractions = attractionsData.filter((a: any) => a.IsMustVisit);

  return (
    <>
      <Banner BannerList={banner} />

      {/* Category Filters */}
      <div className="sticky top-24 z-40 bg-white/95 backdrop-blur-lg border-b border-[#D8C4A8]/30 shadow-md">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex overflow-x-auto gap-3 py-6 scrollbar-hide">
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                className={`px-6 py-3 rounded-full font-medium whitespace-nowrap transition-all ${selectedCategory === category ? "bg-gradient-to-r from-[#7A4A1D] to-[#5A3A1E] text-white shadow-lg scale-105" : "bg-[#F3EBDD] text-[#5A3A1E] hover:bg-[#D8C4A8] hover:shadow-md"}`}
              >
                {category}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Featured Attractions */}
      {featuredAttractions?.length > 0 && (
        <section className="py-20 px-6 lg:px-8 bg-gradient-to-b from-[#F3EBDD] to-white">
          <div className="max-w-7xl mx-auto">
            <h2 className="text-center mb-16 text-[#5A3A1E]">
              Must-Visit Attractions
            </h2>
            <div className="grid md:grid-cols-3 gap-8">
              {featuredAttractions?.map((attraction: any) => (
                <Link
                  key={attraction.ID}
                  href={`/attractions/${attraction.AttractionURL}/`}
                  className="group relative overflow-hidden rounded-3xl shadow-xl hover:shadow-2xl transition-all duration-500 hover:scale-105"
                >
                  <div className="aspect-[4/3] overflow-hidden">
                    <OptimizedImage
                      src={attraction.MainImage || "#"}
                      alt={attraction.Title || "Attraction Image"}
                      width={600}
                      height={400}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                    />
                  </div>
                  <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent" />
                  <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
                    <h3 className="text-2xl mb-2 font-bold">
                      {attraction.Title}
                    </h3>
                    <div className="flex items-center gap-4 text-sm mb-3">
                      <span className="flex items-center gap-1">
                        <MapPin className="w-4 h-4" />
                        {attraction.Distance}
                      </span>
                      <span className="flex items-center gap-1">
                        <Clock className="w-4 h-4" />
                        {attraction.TravelTime}
                      </span>
                    </div>
                    <button className="bg-[#E07A2F] text-white px-6 py-2 rounded-full text-sm font-medium hover:bg-[#c96a28] transition-colors">
                      Explore
                    </button>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Attractions Grid */}
      <section className="py-20 px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-center mb-16 text-[#5A3A1E]">
            {selectedCategory === "All" ? "All Attractions" : selectedCategory}
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            {filteredAttractions?.map((attraction: any) => (
              <Link
                key={attraction.ID}
                href={`/attractions/${attraction.AttractionURL}/`}
                className="group bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 flex flex-col"
              >
                <div className="aspect-[4/3] overflow-hidden">
                  <OptimizedImage
                    src={attraction.MainImage || "#"}
                    alt={attraction.Title || "Attraction Image"}
                    width={600}
                    height={400}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                  />
                </div>
                <div className="p-6 flex-1 flex flex-col">
                  <div className="flex items-center justify-between mb-3">
                    <span className="px-3 py-1 bg-[#F3EBDD] text-[#BF5500] rounded-full text-sm font-medium flex items-center gap-1">
                      <Tag className="w-3 h-3" />
                      {attraction.AttractionCategory}
                    </span>
                  </div>
                  <h3 className="text-xl font-bold text-[#5A3A1E] mb-2">
                    {attraction.Title}
                  </h3>
                  <p className="text-[#5A3A1E]/85 mb-4 text-sm flex-1">
                    {attraction.TagLine}
                  </p>
                  <div className="flex items-center justify-between text-sm text-[#5A3A1E]/85">
                    <span className="flex items-center gap-1">
                      <MapPin className="w-4 h-4" />
                      {attraction.Distance}
                    </span>
                    <span className="flex items-center gap-1">
                      <Clock className="w-4 h-4" />
                      {attraction.TravelTime}
                    </span>
                  </div>
                  <button className="mt-4 w-full bg-gradient-to-r from-[#7A4A1D] to-[#5A3A1E] text-white py-2 rounded-full font-medium hover:shadow-lg transition-all">
                    View Details
                  </button>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {lookUpCMSData?.["Attractions - Map Section"]?.Template && (
        <section className="py-20 px-6 lg:px-8 bg-[#F3EBDD]">
          <div className="max-w-7xl mx-auto">
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div
                dangerouslySetInnerHTML={{
                  __html: cleanHeaderTags(
                    decodeHtml(
                      lookUpCMSData?.["Attractions - Map Section"]?.Template,
                    ),
                  ),
                }}
              />
              <div className="h-full">
                <OptimizedImage
                  src={lookUpCMSData?.["Attractions - Map Section"]?.CMSImage}
                  alt={
                    lookUpCMSData?.["Attractions - Map Section"]
                      ?.CMSImageAlterTag || "Map"
                  }
                  width={800}
                  height={600}
                  className="w-full h-full object-cover rounded-3xl"
                />
              </div>
            </div>
          </div>
        </section>
      )}
      {lookUpCMSData?.["Attractions - Why Stay at Gir Aranya"] && (
        <div
          dangerouslySetInnerHTML={{
            __html: cleanHeaderTags(
              decodeHtml(
                lookUpCMSData?.["Attractions - Why Stay at Gir Aranya"]
                  ?.Template,
              ),
            ),
          }}
        />
      )}
      {lookUpCMSData?.["Attractions - Listing CTA"] && (
        <div
          dangerouslySetInnerHTML={{
            __html: cleanHeaderTags(
              decodeHtml(
                lookUpCMSData?.["Attractions - Listing CTA"]?.Template,
              ),
            ),
          }}
        />
      )}
    </>
  );
}
